import React from 'react';
import {
  Section,
  Row,
  CopyableValue,
  formatDate,
  formatDuration,
  formatMemory,
  LANG_CLASS_MAP,
} from 'components/JobsTable/JobsDetailsSidebar/JobDetailsSidebarHelpers';
import { JobState, MVLanguageEnum } from 'types/types';
import { JobStatesWorkflow } from 'components/JobStatesWorkflow/JobStatesWorkflow';

interface JobMetadataOverviewProps {
  jobDetails: any; // Ideally concrete JobDetails Type from src/types
  showArcaneMetrics?: boolean;
  renderArcaneSection?: React.ReactNode;
}

export function JobMetadataOverview({
  jobDetails,
  showArcaneMetrics,
  renderArcaneSection,
}: JobMetadataOverviewProps) {
  return (
    <div className="job-meta-display">
      <Section title="Identity">
        <Row label="Job ID">
          <CopyableValue value={jobDetails.sparkJobId} />
        </Row>
        <Row label="App ID">
          <CopyableValue value={jobDetails.sparkAppId} />
        </Row>
        <Row label="Table">
          <span className="job-meta-display__mono">
            {jobDetails.schemaName}.{jobDetails.tableName}
          </span>
        </Row>
        <Row label="Schema">
          <span className="job-meta-display__mono">
            {jobDetails.schemaName}
          </span>
        </Row>
        <Row label="Cluster">
          <span className="job-meta-display__mono">
            {jobDetails.clusterName}
          </span>
        </Row>
        <Row label="Cluster Prefix / URL">
          <span className="job-meta-display__mono">
            {jobDetails.clusterPrefix} / {jobDetails.clusterUrl}
          </span>
        </Row>
        <Row label="Vendor">{jobDetails.vendor}</Row>
      </Section>

      <div className="job-meta-display__divider" />

      <Section title="Timing">
        <Row label="Created">{formatDate(jobDetails.createdAt)}</Row>
        <Row label="Updated">{formatDate(jobDetails?.updatedAt ?? null)}</Row>
        <Row label="Dequeued">{formatDate(jobDetails?.dequeuedAt ?? null)}</Row>
        <Row label="Duration">
          <span className="job-meta-display__duration">
            {formatDuration(jobDetails.duration)}
          </span>
        </Row>
      </Section>

      <div className="job-meta-display__divider" />

      <Section title="Resources">
        <Row label="Language">
          <span
            className={`job-meta-display__lang-pill ${LANG_CLASS_MAP[jobDetails.mvLanguage as MVLanguageEnum] ?? 'lang-pill--default'}`}>
            {jobDetails?.mvLanguage ?? '—'}
          </span>
        </Row>
        <Row label="Executors">{jobDetails.totalExecutorCount}</Row>
        <Row label="Peak memory">{formatMemory(jobDetails.peakMemoryInMB)}</Row>
        <Row label="Driver memory">
          {formatMemory(jobDetails.sparkDriverMemoryInMB)}
        </Row>
      </Section>

      {showArcaneMetrics && renderArcaneSection && (
        <>
          <div className="job-meta-display__divider" />
          <Section title="Infrastructure metrics">
            {renderArcaneSection}
          </Section>
        </>
      )}

      <div className="job-meta-display__divider" />

      <Section title="Incorta Data Processing">
        <Row label="Job State">{JobState[Number(jobDetails.jobState)]}</Row>
        <Row label="Extracted Row Count">
          {jobDetails.extractedRowCount?.toLocaleString() ?? '—'}
        </Row>
        <Row label="Rejected Row Count">
          {jobDetails.rejectedRowCount?.toLocaleString() ?? '—'}
        </Row>
        <Row label="Loaded Row Count">
          {jobDetails.loadedRowCount?.toLocaleString() ?? '—'}
        </Row>
        {jobDetails.jobStates && jobDetails.jobStates.length > 0 && (
          <Row label="Job States">
            <JobStatesWorkflow jobStates={jobDetails.jobStates} />
          </Row>
        )}
      </Section>
    </div>
  );
}
